/**
 * BrokerProfileManager Component
 *
 * Provides a CRUD interface for managing broker commission profiles under the
 * exchange-keyed schedule model.
 *
 * Features:
 * - Display list of active broker profiles as a table (with a schedule-count summary)
 * - Add new profile via inline form containing a schedule editor
 * - Edit existing profiles
 * - Deactivate profiles (with explanation about referenced trades)
 * - Per-(exchange, instrumentClass) schedule rows with add/edit/remove
 * - Client-side validation mirroring shared validateBrokerProfileInput
 *
 * Validates: Requirements 12.3, 12.4
 */

import { useState, useEffect, useCallback, Fragment } from 'react';
import {
  BrokerProfile,
  BrokerProfileInput,
  CommissionScheduleInput,
  Exchange,
  InstrumentClass,
  validateBrokerProfileInput,
  type ValidationError,
} from '@covered-calls/shared';
import { apiClient, ApiClientError } from '../services/api-client';

// ─── Types ──────────────────────────────────────────────────────────────────

/**
 * A single schedule row in the editor. All numeric fields are kept as strings
 * while editing so the inputs behave naturally; they are parsed on submit.
 */
interface ScheduleRowState {
  exchange: Exchange;
  instrumentClass: InstrumentClass;
  flatFee: string;
  percentageRate: string;
  perUnitFee: string;
  exerciseFeePerContract: string;
  gstEnabled: boolean;
  gstRate: string;
  currency: string;
}

interface FormState {
  brokerName: string;
  schedules: ScheduleRowState[];
}

const DEFAULT_CURRENCY_FOR_EXCHANGE: Record<Exchange, string> = {
  [Exchange.ASX]: 'AUD',
  [Exchange.US]: 'USD',
};

const createEmptyScheduleRow = (
  exchange: Exchange = Exchange.ASX,
  instrumentClass: InstrumentClass = InstrumentClass.SHARES
): ScheduleRowState => ({
  exchange,
  instrumentClass,
  flatFee: '',
  percentageRate: '',
  perUnitFee: '',
  exerciseFeePerContract: '',
  gstEnabled: exchange === Exchange.ASX,
  gstRate: '10',
  currency: DEFAULT_CURRENCY_FOR_EXCHANGE[exchange] ?? '',
});

const EMPTY_FORM: FormState = {
  brokerName: '',
  schedules: [createEmptyScheduleRow()],
};

/** Parse a string field to a number, treating blank as 0. */
const parseNumber = (value: string): number => {
  const trimmed = value.trim();
  if (trimmed === '') return 0;
  const parsed = parseFloat(trimmed);
  return isNaN(parsed) ? NaN : parsed;
};

/** Convert a schedule row (string fields) into a CommissionScheduleInput. */
const rowToScheduleInput = (row: ScheduleRowState): CommissionScheduleInput => {
  const perUnitTrimmed = row.perUnitFee.trim();
  const exerciseFeeTrimmed = row.exerciseFeePerContract.trim();
  return {
    exchange: row.exchange,
    instrumentClass: row.instrumentClass,
    flatFee: parseNumber(row.flatFee),
    percentageRate: parseNumber(row.percentageRate),
    perUnitFee: perUnitTrimmed === '' ? undefined : parseNumber(perUnitTrimmed),
    exerciseFeePerContract: exerciseFeeTrimmed === '' ? undefined : parseNumber(exerciseFeeTrimmed),
    gstEnabled: row.gstEnabled,
    gstRate: parseNumber(row.gstRate),
    currency: row.currency.trim(),
  };
};

/** Convert a stored schedule into an editable row. */
const scheduleToRow = (schedule: CommissionScheduleInput): ScheduleRowState => ({
  exchange: schedule.exchange,
  instrumentClass: schedule.instrumentClass,
  flatFee: schedule.flatFee ? schedule.flatFee.toString() : '',
  percentageRate: schedule.percentageRate ? schedule.percentageRate.toString() : '',
  perUnitFee:
    schedule.perUnitFee !== undefined && schedule.perUnitFee !== null
      ? schedule.perUnitFee.toString()
      : '',
  exerciseFeePerContract:
    schedule.exerciseFeePerContract !== undefined && schedule.exerciseFeePerContract !== null
      ? schedule.exerciseFeePerContract.toString()
      : '',
  gstEnabled: schedule.gstEnabled,
  gstRate: schedule.gstRate != null ? schedule.gstRate.toString() : '',
  currency: schedule.currency ?? '',
});

// ─── Styles ─────────────────────────────────────────────────────────────────

const styles = {
  container: {
    maxWidth: '960px',
    margin: '0 auto',
    padding: '24px',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  } as React.CSSProperties,
  heading: {
    fontSize: '1.5rem',
    fontWeight: 600,
    marginBottom: '8px',
  } as React.CSSProperties,
  description: {
    fontSize: '0.85rem',
    color: 'var(--color-muted)',
    marginBottom: '24px',
  } as React.CSSProperties,
  addBtn: {
    padding: '10px 18px',
    background: 'var(--color-brand)',
    color: 'var(--color-on-brand)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.9rem',
    fontWeight: 500,
    cursor: 'pointer',
    marginBottom: '20px',
    transition: 'background 0.15s ease',
  } as React.CSSProperties,
  table: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.85rem',
    marginBottom: '24px',
  } as React.CSSProperties,
  th: {
    textAlign: 'left' as const,
    padding: '10px 12px',
    borderBottom: '2px solid var(--color-border)',
    fontWeight: 600,
    color: 'var(--color-text)',
    fontSize: '0.8rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.025em',
  } as React.CSSProperties,
  td: {
    padding: '10px 12px',
    borderBottom: '1px solid var(--color-border)',
    color: 'var(--color-text)',
  } as React.CSSProperties,
  actionBtn: (variant: 'edit' | 'deactivate') => ({
    padding: '4px 10px',
    border: variant === 'edit' ? '1px solid var(--color-input-border)' : '1px solid var(--color-chip-danger-text)',
    borderRadius: '4px',
    fontSize: '0.78rem',
    cursor: 'pointer',
    background: variant === 'edit' ? 'var(--color-surface)' : 'var(--color-chip-danger-bg)',
    color: variant === 'edit' ? 'var(--color-text)' : 'var(--color-chip-danger-text)',
    marginRight: '6px',
    transition: 'background 0.15s ease',
  } as React.CSSProperties),
  formContainer: {
    background: 'var(--color-surface-2)',
    border: '1px solid var(--color-border)',
    borderRadius: '8px',
    padding: '20px',
    marginBottom: '20px',
  } as React.CSSProperties,
  formHeading: {
    fontSize: '1rem',
    fontWeight: 600,
    color: 'var(--color-text)',
    marginBottom: '16px',
  } as React.CSSProperties,
  fieldGroup: {
    marginBottom: '14px',
  } as React.CSSProperties,
  label: {
    display: 'block',
    fontSize: '0.85rem',
    fontWeight: 500,
    color: 'var(--color-text)',
    marginBottom: '4px',
  } as React.CSSProperties,
  input: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--color-input-border)',
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    outline: 'none',
    transition: 'border-color 0.15s ease',
  } as React.CSSProperties,
  inputError: {
    borderColor: 'var(--color-danger)',
  } as React.CSSProperties,
  select: {
    width: '100%',
    padding: '8px 12px',
    border: '1px solid var(--color-input-border)',
    borderRadius: '6px',
    fontSize: '0.9rem',
    boxSizing: 'border-box' as const,
    background: 'var(--color-surface)',
  } as React.CSSProperties,
  errorText: {
    color: 'var(--color-danger)',
    fontSize: '0.8rem',
    marginTop: '4px',
  } as React.CSSProperties,
  formError: {
    background: 'var(--color-chip-danger-bg)',
    border: '1px solid var(--color-chip-danger-text)',
    borderRadius: '6px',
    padding: '12px',
    marginBottom: '16px',
    color: 'var(--color-chip-danger-text)',
    fontSize: '0.85rem',
  } as React.CSSProperties,
  formActions: {
    display: 'flex',
    gap: '8px',
    marginTop: '16px',
  } as React.CSSProperties,
  saveBtn: {
    padding: '8px 16px',
    background: 'var(--color-brand)',
    color: 'var(--color-on-brand)',
    border: 'none',
    borderRadius: '6px',
    fontSize: '0.85rem',
    fontWeight: 500,
    cursor: 'pointer',
  } as React.CSSProperties,
  saveBtnDisabled: {
    background: 'var(--color-brand-disabled)',
    cursor: 'not-allowed',
  } as React.CSSProperties,
  cancelBtn: {
    padding: '8px 16px',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    border: '1px solid var(--color-input-border)',
    borderRadius: '6px',
    fontSize: '0.85rem',
    cursor: 'pointer',
  } as React.CSSProperties,
  // Schedule editor styles
  schedulesHeading: {
    fontSize: '0.9rem',
    fontWeight: 600,
    color: 'var(--color-text)',
    marginBottom: '4px',
  } as React.CSSProperties,
  schedulesHint: {
    fontSize: '0.78rem',
    color: 'var(--color-muted)',
    marginBottom: '12px',
  } as React.CSSProperties,
  scheduleCard: {
    background: 'var(--color-surface)',
    border: '1px solid var(--color-border)',
    borderRadius: '6px',
    padding: '14px',
    marginBottom: '12px',
  } as React.CSSProperties,
  scheduleCardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '10px',
  } as React.CSSProperties,
  scheduleCardTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: 'var(--color-text)',
  } as React.CSSProperties,
  removeRowBtn: {
    padding: '3px 9px',
    border: '1px solid var(--color-chip-danger-text)',
    borderRadius: '4px',
    fontSize: '0.75rem',
    cursor: 'pointer',
    background: 'var(--color-chip-danger-bg)',
    color: 'var(--color-chip-danger-text)',
  } as React.CSSProperties,
  scheduleGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))',
    gap: '10px',
  } as React.CSSProperties,
  gstRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
    fontSize: '0.85rem',
    color: 'var(--color-text)',
  } as React.CSSProperties,
  addRowBtn: {
    padding: '8px 14px',
    background: 'var(--color-surface)',
    color: 'var(--color-brand)',
    border: '1px dashed var(--color-brand-disabled)',
    borderRadius: '6px',
    fontSize: '0.82rem',
    fontWeight: 500,
    cursor: 'pointer',
    marginBottom: '8px',
  } as React.CSSProperties,
  smallLabel: {
    display: 'block',
    fontSize: '0.78rem',
    fontWeight: 500,
    color: 'var(--color-muted)',
    marginBottom: '3px',
  } as React.CSSProperties,
  confirmDialog: {
    background: 'var(--color-chip-warning-bg)',
    border: '1px solid var(--color-chip-warning-text)',
    borderRadius: '6px',
    padding: '16px',
    marginBottom: '16px',
  } as React.CSSProperties,
  confirmText: {
    color: 'var(--color-chip-warning-text)',
    fontSize: '0.85rem',
    marginBottom: '12px',
  } as React.CSSProperties,
  confirmNote: {
    color: 'var(--color-chip-warning-text)',
    fontSize: '0.8rem',
    fontStyle: 'italic' as const,
    marginBottom: '12px',
  } as React.CSSProperties,
  confirmActions: {
    display: 'flex',
    gap: '8px',
  } as React.CSSProperties,
  emptyState: {
    textAlign: 'center' as const,
    padding: '40px',
    color: 'var(--color-muted)',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  loadingState: {
    textAlign: 'center' as const,
    padding: '40px',
    color: 'var(--color-muted)',
    fontSize: '0.9rem',
  } as React.CSSProperties,
  // Expandable schedule detail styles
  expandBtn: {
    padding: '4px 10px',
    border: '1px solid var(--color-input-border)',
    borderRadius: '4px',
    fontSize: '0.78rem',
    cursor: 'pointer',
    background: 'var(--color-surface)',
    color: 'var(--color-text)',
    marginRight: '6px',
    transition: 'background 0.15s ease',
  } as React.CSSProperties,
  expandedCell: {
    padding: '12px 16px',
    background: 'var(--color-surface-2)',
    borderBottom: '1px solid var(--color-border)',
  } as React.CSSProperties,
  nestedTable: {
    width: '100%',
    borderCollapse: 'collapse' as const,
    fontSize: '0.8rem',
  } as React.CSSProperties,
  nestedTh: {
    textAlign: 'left' as const,
    padding: '6px 10px',
    borderBottom: '1px solid var(--color-border)',
    fontWeight: 600,
    color: 'var(--color-muted)',
    fontSize: '0.72rem',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.02em',
  } as React.CSSProperties,
  nestedTd: {
    padding: '6px 10px',
    borderBottom: '1px solid var(--color-border)',
    color: 'var(--color-text)',
  } as React.CSSProperties,
  nestedEmpty: {
    padding: '8px 10px',
    color: 'var(--color-muted)',
    fontStyle: 'italic' as const,
    fontSize: '0.8rem',
  } as React.CSSProperties,
};

// ─── Helpers ─────────────────────────────────────────────────────────────────

/** Human-readable summary for a profile's schedule count in the list. */
const scheduleSummary = (profile: BrokerProfile): string => {
  const count = profile.schedules?.length ?? 0;
  if (count === 0) return 'Needs re-entry';
  return `${count} schedule${count === 1 ? '' : 's'}`;
};

// ─── Component ──────────────────────────────────────────────────────────────

export function BrokerProfileManager() {
  // Profile list state
  const [profiles, setProfiles] = useState<BrokerProfile[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  // Form state
  const [showForm, setShowForm] = useState(false);
  const [editingProfileId, setEditingProfileId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<ValidationError[]>([]);
  const [submitError, setSubmitError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Deactivation confirmation state
  const [deactivatingId, setDeactivatingId] = useState<string | null>(null);
  const [isDeactivating, setIsDeactivating] = useState(false);

  // Expandable schedule detail state
  const [expandedProfileId, setExpandedProfileId] = useState<string | null>(null);

  const toggleExpanded = (profileId: string) => {
    setExpandedProfileId((prev) => (prev === profileId ? null : profileId));
  };

  // ─── Data Loading ───────────────────────────────────────────────────────

  const loadProfiles = useCallback(async () => {
    setIsLoading(true);
    setLoadError('');
    try {
      const data = await apiClient.get<BrokerProfile[]>('/broker-profiles');
      setProfiles(data.filter((p) => p.isActive));
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to load broker profiles.';
      setLoadError(message);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // ─── Form Helpers ─────────────────────────────────────────────────────

  const getFieldError = (field: string): string | undefined => {
    return errors.find((e) => e.field === field)?.message;
  };

  const openAddForm = () => {
    setForm({ brokerName: '', schedules: [createEmptyScheduleRow()] });
    setEditingProfileId(null);
    setErrors([]);
    setSubmitError('');
    setShowForm(true);
  };

  const openEditForm = (profile: BrokerProfile) => {
    const rows = (profile.schedules ?? []).map(scheduleToRow);
    setForm({
      brokerName: profile.brokerName,
      schedules: rows.length > 0 ? rows : [createEmptyScheduleRow()],
    });
    setEditingProfileId(profile.profileId);
    setErrors([]);
    setSubmitError('');
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    setEditingProfileId(null);
    setForm(EMPTY_FORM);
    setErrors([]);
    setSubmitError('');
  };

  // ─── Schedule Row Editing ─────────────────────────────────────────────

  const addScheduleRow = () => {
    setForm((prev) => ({
      ...prev,
      schedules: [...prev.schedules, createEmptyScheduleRow()],
    }));
  };

  const removeScheduleRow = (index: number) => {
    setForm((prev) => ({
      ...prev,
      schedules: prev.schedules.filter((_, i) => i !== index),
    }));
  };

  const updateScheduleRow = (index: number, patch: Partial<ScheduleRowState>) => {
    setForm((prev) => ({
      ...prev,
      schedules: prev.schedules.map((row, i) => (i === index ? { ...row, ...patch } : row)),
    }));
  };

  const handleExchangeChange = (index: number, exchange: Exchange) => {
    // Default the currency/GST to sensible values for the chosen exchange, but
    // only when the current currency matches a known default (so we don't clobber
    // a currency the user typed manually).
    setForm((prev) => ({
      ...prev,
      schedules: prev.schedules.map((row, i) => {
        if (i !== index) return row;
        const wasDefaultCurrency =
          row.currency === '' ||
          row.currency === DEFAULT_CURRENCY_FOR_EXCHANGE[row.exchange];
        return {
          ...row,
          exchange,
          currency: wasDefaultCurrency
            ? DEFAULT_CURRENCY_FOR_EXCHANGE[exchange] ?? row.currency
            : row.currency,
        };
      }),
    }));
  };

  // ─── Form Submission ──────────────────────────────────────────────────

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      setSubmitError('');

      // Build input for validation
      const input: BrokerProfileInput = {
        brokerName: form.brokerName.trim(),
        schedules: form.schedules.map(rowToScheduleInput),
      };

      // Client-side validation (mirrors the shared server rules).
      const validationErrors = validateBrokerProfileInput(input);
      setErrors(validationErrors);
      if (validationErrors.length > 0) return;

      setIsSubmitting(true);

      try {
        if (editingProfileId) {
          // Update existing profile
          await apiClient.put(`/broker-profiles/${editingProfileId}`, input);
        } else {
          // Create new profile
          await apiClient.post('/broker-profiles', input);
        }

        closeForm();
        await loadProfiles();
      } catch (err: unknown) {
        if (err instanceof ApiClientError) {
          if (err.errors && err.errors.length > 0) {
            setErrors(err.errors as ValidationError[]);
          } else {
            setSubmitError(err.message || 'Failed to save profile. Please try again.');
          }
        } else {
          setSubmitError('An unexpected error occurred. Please try again.');
        }
      } finally {
        setIsSubmitting(false);
      }
    },
    [form, editingProfileId, loadProfiles]
  );

  // ─── Deactivation ────────────────────────────────────────────────────

  const handleDeactivateClick = (profileId: string) => {
    setDeactivatingId(profileId);
  };

  const cancelDeactivation = () => {
    setDeactivatingId(null);
  };

  const confirmDeactivation = useCallback(async () => {
    if (!deactivatingId) return;

    setIsDeactivating(true);
    try {
      await apiClient.delete(`/broker-profiles/${deactivatingId}`);
      setDeactivatingId(null);
      await loadProfiles();
    } catch (err) {
      const message =
        err instanceof ApiClientError
          ? err.message
          : 'Failed to deactivate profile.';
      setSubmitError(message);
      setDeactivatingId(null);
    } finally {
      setIsDeactivating(false);
    }
  }, [deactivatingId, loadProfiles]);

  // ─── Render: Loading State ────────────────────────────────────────────

  if (isLoading) {
    return (
      <div style={styles.container}>
        <p style={styles.loadingState}>Loading broker profiles...</p>
      </div>
    );
  }

  // ─── Render ───────────────────────────────────────────────────────────

  return (
    <div style={styles.container}>
      <h2 style={styles.heading}>Broker Commission Profiles</h2>
      <p style={styles.description}>
        Manage your broker commission profiles. Each profile holds a fee schedule per
        exchange and instrument class, used to auto-populate commissions when creating trades.
      </p>

      {/* Load error */}
      {loadError && (
        <div style={styles.formError} role="alert">
          {loadError}
          <button
            onClick={loadProfiles}
            style={{ ...styles.cancelBtn, marginLeft: '12px', padding: '4px 10px', fontSize: '0.78rem' }}
            type="button"
          >
            Retry
          </button>
        </div>
      )}

      {/* Deactivation confirmation dialog */}
      {deactivatingId && (
        <div style={styles.confirmDialog} role="alertdialog" aria-labelledby="deactivate-heading">
          <p id="deactivate-heading" style={styles.confirmText}>
            <strong>Deactivate this broker profile?</strong>
          </p>
          <p style={styles.confirmNote}>
            Deactivated profiles will remain on existing trades but will no longer appear for selection on new trades.
          </p>
          <div style={styles.confirmActions}>
            <button
              onClick={confirmDeactivation}
              disabled={isDeactivating}
              style={{
                ...styles.actionBtn('deactivate'),
                opacity: isDeactivating ? 0.6 : 1,
              }}
              type="button"
              aria-label="Confirm deactivation"
            >
              {isDeactivating ? 'Deactivating...' : 'Yes, Deactivate'}
            </button>
            <button
              onClick={cancelDeactivation}
              disabled={isDeactivating}
              style={styles.cancelBtn}
              type="button"
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Add button (hidden when form is open) */}
      {!showForm && (
        <button
          onClick={openAddForm}
          style={styles.addBtn}
          type="button"
          aria-label="Add new broker profile"
        >
          Add New Profile
        </button>
      )}

      {/* Inline form for add/edit */}
      {showForm && (
        <div style={styles.formContainer}>
          <h3 style={styles.formHeading}>
            {editingProfileId ? 'Edit Broker Profile' : 'New Broker Profile'}
          </h3>

          {submitError && (
            <div style={styles.formError} role="alert">
              {submitError}
            </div>
          )}

          {/* Top-level schedules error (empty schedules / duplicate pairs) */}
          {getFieldError('schedules') && (
            <div style={styles.formError} role="alert">
              {getFieldError('schedules')}
            </div>
          )}

          <form onSubmit={handleSubmit} noValidate>
            <div style={styles.fieldGroup}>
              <label htmlFor="brokerName" style={styles.label}>
                Broker Name *
              </label>
              <input
                id="brokerName"
                type="text"
                value={form.brokerName}
                onChange={(e) => setForm({ ...form, brokerName: e.target.value })}
                placeholder="e.g. Interactive Brokers"
                style={{
                  ...styles.input,
                  ...(getFieldError('brokerName') ? styles.inputError : {}),
                }}
                aria-invalid={!!getFieldError('brokerName')}
                aria-describedby={getFieldError('brokerName') ? 'brokerName-error' : undefined}
              />
              {getFieldError('brokerName') && (
                <p id="brokerName-error" style={styles.errorText}>
                  {getFieldError('brokerName')}
                </p>
              )}
            </div>

            {/* ─── Schedule editor ─── */}
            <div style={styles.fieldGroup}>
              <p style={styles.schedulesHeading}>Commission Schedules *</p>
              <p style={styles.schedulesHint}>
                Add one schedule per exchange / instrument class. Each row uses whichever
                fee is greater across flat, percentage, and per-unit components.
              </p>

              {form.schedules.map((row, i) => {
                const prefix = `schedules[${i}]`;
                const rowError = getFieldError(prefix);
                return (
                  <div key={i} style={styles.scheduleCard}>
                    <div style={styles.scheduleCardHeader}>
                      <span style={styles.scheduleCardTitle}>Schedule {i + 1}</span>
                      <button
                        type="button"
                        onClick={() => removeScheduleRow(i)}
                        style={styles.removeRowBtn}
                        aria-label={`Remove schedule ${i + 1}`}
                      >
                        Remove
                      </button>
                    </div>

                    <div style={styles.scheduleGrid}>
                      <div>
                        <label htmlFor={`${prefix}-exchange`} style={styles.smallLabel}>
                          Exchange
                        </label>
                        <select
                          id={`${prefix}-exchange`}
                          value={row.exchange}
                          onChange={(e) => handleExchangeChange(i, e.target.value as Exchange)}
                          style={styles.select}
                        >
                          {Object.values(Exchange).map((ex) => (
                            <option key={ex} value={ex}>
                              {ex}
                            </option>
                          ))}
                        </select>
                        {getFieldError(`${prefix}.exchange`) && (
                          <p style={styles.errorText}>{getFieldError(`${prefix}.exchange`)}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`${prefix}-instrumentClass`} style={styles.smallLabel}>
                          Instrument Class
                        </label>
                        <select
                          id={`${prefix}-instrumentClass`}
                          value={row.instrumentClass}
                          onChange={(e) =>
                            updateScheduleRow(i, {
                              instrumentClass: e.target.value as InstrumentClass,
                            })
                          }
                          style={styles.select}
                        >
                          {Object.values(InstrumentClass).map((ic) => (
                            <option key={ic} value={ic}>
                              {ic}
                            </option>
                          ))}
                        </select>
                        {getFieldError(`${prefix}.instrumentClass`) && (
                          <p style={styles.errorText}>
                            {getFieldError(`${prefix}.instrumentClass`)}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`${prefix}-flatFee`} style={styles.smallLabel}>
                          Flat Fee
                        </label>
                        <input
                          id={`${prefix}-flatFee`}
                          type="number"
                          value={row.flatFee}
                          onChange={(e) => updateScheduleRow(i, { flatFee: e.target.value })}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          style={{
                            ...styles.input,
                            ...(getFieldError(`${prefix}.flatFee`) ? styles.inputError : {}),
                          }}
                          aria-invalid={!!getFieldError(`${prefix}.flatFee`)}
                        />
                        {getFieldError(`${prefix}.flatFee`) && (
                          <p style={styles.errorText}>{getFieldError(`${prefix}.flatFee`)}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`${prefix}-percentageRate`} style={styles.smallLabel}>
                          Percentage Rate (%)
                        </label>
                        <input
                          id={`${prefix}-percentageRate`}
                          type="number"
                          value={row.percentageRate}
                          onChange={(e) =>
                            updateScheduleRow(i, { percentageRate: e.target.value })
                          }
                          placeholder="0.30"
                          min="0"
                          step="0.001"
                          style={{
                            ...styles.input,
                            ...(getFieldError(`${prefix}.percentageRate`)
                              ? styles.inputError
                              : {}),
                          }}
                          aria-invalid={!!getFieldError(`${prefix}.percentageRate`)}
                        />
                        {getFieldError(`${prefix}.percentageRate`) && (
                          <p style={styles.errorText}>
                            {getFieldError(`${prefix}.percentageRate`)}
                          </p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`${prefix}-perUnitFee`} style={styles.smallLabel}>
                          {row.instrumentClass === InstrumentClass.OPTIONS
                            ? 'Per Contract Fee'
                            : 'Per Share Fee'}
                        </label>
                        <input
                          id={`${prefix}-perUnitFee`}
                          type="number"
                          value={row.perUnitFee}
                          onChange={(e) => updateScheduleRow(i, { perUnitFee: e.target.value })}
                          placeholder="Optional"
                          min="0"
                          step="0.01"
                          style={{
                            ...styles.input,
                            ...(getFieldError(`${prefix}.perUnitFee`) ? styles.inputError : {}),
                          }}
                          aria-invalid={!!getFieldError(`${prefix}.perUnitFee`)}
                        />
                        {getFieldError(`${prefix}.perUnitFee`) && (
                          <p style={styles.errorText}>{getFieldError(`${prefix}.perUnitFee`)}</p>
                        )}
                      </div>

                      {/* Option exercise/assignment fee per contract — only
                          applicable to OPTIONS legs (e.g. ASX IBKR $0.275/contract).
                          Additive on exercise; GST applies on top when enabled. */}
                      {row.instrumentClass === InstrumentClass.OPTIONS && (
                        <div>
                          <label htmlFor={`${prefix}-exerciseFeePerContract`} style={styles.smallLabel}>
                            Exercise Fee / Contract
                          </label>
                          <input
                            id={`${prefix}-exerciseFeePerContract`}
                            type="number"
                            value={row.exerciseFeePerContract}
                            onChange={(e) => updateScheduleRow(i, { exerciseFeePerContract: e.target.value })}
                            placeholder="Optional (e.g. 0.275)"
                            min="0"
                            step="0.001"
                            style={{
                              ...styles.input,
                              ...(getFieldError(`${prefix}.exerciseFeePerContract`) ? styles.inputError : {}),
                            }}
                            aria-invalid={!!getFieldError(`${prefix}.exerciseFeePerContract`)}
                          />
                          {getFieldError(`${prefix}.exerciseFeePerContract`) && (
                            <p style={styles.errorText}>{getFieldError(`${prefix}.exerciseFeePerContract`)}</p>
                          )}
                        </div>
                      )}

                      <div>
                        <label htmlFor={`${prefix}-currency`} style={styles.smallLabel}>
                          Currency
                        </label>
                        <input
                          id={`${prefix}-currency`}
                          type="text"
                          value={row.currency}
                          onChange={(e) => updateScheduleRow(i, { currency: e.target.value })}
                          placeholder="AUD"
                          maxLength={3}
                          style={{
                            ...styles.input,
                            ...(getFieldError(`${prefix}.currency`) ? styles.inputError : {}),
                          }}
                          aria-invalid={!!getFieldError(`${prefix}.currency`)}
                        />
                        {getFieldError(`${prefix}.currency`) && (
                          <p style={styles.errorText}>{getFieldError(`${prefix}.currency`)}</p>
                        )}
                      </div>

                      <div>
                        <label htmlFor={`${prefix}-gstRate`} style={styles.smallLabel}>
                          GST Rate (%)
                        </label>
                        <input
                          id={`${prefix}-gstRate`}
                          type="number"
                          value={row.gstRate}
                          onChange={(e) => updateScheduleRow(i, { gstRate: e.target.value })}
                          placeholder="10"
                          min="0"
                          max="100"
                          step="0.01"
                          disabled={!row.gstEnabled}
                          style={{
                            ...styles.input,
                            ...(getFieldError(`${prefix}.gstRate`) ? styles.inputError : {}),
                            ...(!row.gstEnabled ? { background: 'var(--color-surface-2)' } : {}),
                          }}
                          aria-invalid={!!getFieldError(`${prefix}.gstRate`)}
                        />
                        {getFieldError(`${prefix}.gstRate`) && (
                          <p style={styles.errorText}>{getFieldError(`${prefix}.gstRate`)}</p>
                        )}
                      </div>
                    </div>

                    <label style={styles.gstRow}>
                      <input
                        type="checkbox"
                        checked={row.gstEnabled}
                        onChange={(e) => updateScheduleRow(i, { gstEnabled: e.target.checked })}
                      />
                      GST enabled
                    </label>

                    {rowError && <p style={styles.errorText}>{rowError}</p>}
                  </div>
                );
              })}

              <button type="button" onClick={addScheduleRow} style={styles.addRowBtn}>
                + Add Schedule
              </button>
            </div>

            <div style={styles.formActions}>
              <button
                type="submit"
                disabled={isSubmitting}
                style={{
                  ...styles.saveBtn,
                  ...(isSubmitting ? styles.saveBtnDisabled : {}),
                }}
              >
                {isSubmitting
                  ? 'Saving...'
                  : editingProfileId
                    ? 'Update Profile'
                    : 'Create Profile'}
              </button>
              <button
                type="button"
                onClick={closeForm}
                disabled={isSubmitting}
                style={styles.cancelBtn}
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Profiles table */}
      {profiles.length === 0 && !loadError ? (
        <div style={styles.emptyState}>
          <p>No broker profiles yet.</p>
          <p style={{ fontSize: '0.8rem', marginTop: '4px' }}>
            Add a profile to auto-populate commission fields on new trades.
          </p>
        </div>
      ) : profiles.length > 0 ? (
        <table style={styles.table} aria-label="Broker commission profiles">
          <thead>
            <tr>
              <th style={styles.th}>Broker Name</th>
              <th style={styles.th}>Schedules</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {profiles.map((profile) => {
              const isExpanded = expandedProfileId === profile.profileId;
              const schedules = profile.schedules ?? [];
              return (
                <Fragment key={profile.profileId}>
                  <tr>
                    <td style={styles.td}>{profile.brokerName}</td>
                    <td style={styles.td}>{scheduleSummary(profile)}</td>
                    <td style={styles.td}>
                      <button
                        onClick={() => toggleExpanded(profile.profileId)}
                        style={styles.expandBtn}
                        type="button"
                        aria-expanded={isExpanded}
                        aria-label={`${isExpanded ? 'Hide' : 'View'} schedules for ${profile.brokerName}`}
                      >
                        {isExpanded ? 'Hide schedules' : 'View schedules'}
                      </button>
                      <button
                        onClick={() => openEditForm(profile)}
                        style={styles.actionBtn('edit')}
                        type="button"
                        aria-label={`Edit ${profile.brokerName}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeactivateClick(profile.profileId)}
                        style={styles.actionBtn('deactivate')}
                        type="button"
                        aria-label={`Deactivate ${profile.brokerName}`}
                      >
                        Deactivate
                      </button>
                    </td>
                  </tr>
                  {isExpanded && (
                    <tr key={`${profile.profileId}-schedules`}>
                      <td style={styles.expandedCell} colSpan={3}>
                        {schedules.length === 0 ? (
                          <p style={styles.nestedEmpty}>
                            No schedules — this profile needs re-entry.
                          </p>
                        ) : (
                          <table
                            style={styles.nestedTable}
                            aria-label={`${profile.brokerName} commission schedules`}
                          >
                            <thead>
                              <tr>
                                <th style={styles.nestedTh}>Exchange</th>
                                <th style={styles.nestedTh}>Instrument</th>
                                <th style={styles.nestedTh}>Flat Fee</th>
                                <th style={styles.nestedTh}>Rate %</th>
                                <th style={styles.nestedTh}>Per Unit</th>
                                <th style={styles.nestedTh}>Exercise Fee</th>
                                <th style={styles.nestedTh}>GST</th>
                                <th style={styles.nestedTh}>Currency</th>
                              </tr>
                            </thead>
                            <tbody>
                              {schedules.map((schedule, si) => (
                                <tr key={`${schedule.exchange}-${schedule.instrumentClass}-${si}`}>
                                  <td style={styles.nestedTd}>{schedule.exchange}</td>
                                  <td style={styles.nestedTd}>{schedule.instrumentClass}</td>
                                  <td style={styles.nestedTd}>{schedule.flatFee.toFixed(2)}</td>
                                  <td style={styles.nestedTd}>
                                    {schedule.percentageRate.toFixed(3)}%
                                  </td>
                                  <td style={styles.nestedTd}>
                                    {schedule.perUnitFee != null
                                      ? schedule.perUnitFee.toFixed(2)
                                      : '—'}
                                  </td>
                                  <td style={styles.nestedTd}>
                                    {schedule.exerciseFeePerContract != null
                                      ? schedule.exerciseFeePerContract.toFixed(3)
                                      : '—'}
                                  </td>
                                  <td style={styles.nestedTd}>
                                    {schedule.gstEnabled ? `${schedule.gstRate}%` : 'Off'}
                                  </td>
                                  <td style={styles.nestedTd}>{schedule.currency}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        )}
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}
